import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CoreService } from '@charlesschwab/sac-ui-core';
import { EAuthClientsResponse } from '../../../setup-eAuthorization/models/EAuthClients';
import { selectCreateStandingAuthEAuthClients, selectCreateStandingAuthEauthClientServiceError, selectEAuthClients, selectEauthClientServiceError } from '../../../setup-eAuthorization/reducers';
import { Constants, digitalRegTypes, eAuthNotifications, transactionTypes, transferTypes } from '../../utilities/Constants';
import { selectEAuthError } from '../../reducers';
import { EAuthActions } from '../../actions';
import { selectAccountInfo, selectRegType, selectUseTestTrustDigitalToPdfFlow } from '../../../account-info-header/reducers';
import { selectInternalTransfersInfo, selectIs401kToAccount, selectIsBundledOneTimeTotalDist, selectStagedTransactionForm, selectToAccountsInfo, selectTransferType, selectVerifyCallFinished } from '../../../create-request/reducers';
import { InternalTransfersInfo, ToAccountInfoById } from '../../../create-request/models/internal-transfer/InternalTransfersInfo';
import { selectisStandingAuthI401KToAccount, selectSLOAVerifyCallFinished, selectTransactionType } from '../../../standing-authorizations/reducers';
import { FeatureFlagService } from '../../services/feature-flag-service';
import { isItExtraPrompts } from '../../../create-request/models/form/TransactionForm';
import { ValidateWireFlagsService } from '../../services/validate-WireFlags.service';
import { isPalAccountFlow } from '../../utilities/AccountFlow';

@Component({
  standalone: false,
  selector: 'mm-client-authorization',
  templateUrl: './client-authorization.component.html',
  styleUrls: ['./client-authorization.component.scss']
})
export class ClientAuthorizationComponent implements OnInit, OnDestroy {
  @Input() standingAuthorizationsEAuth: boolean = false;
  @Input() clientAuthorizationDetailsVerbiage: string = 'Select a client for ';
  @Input() isInitiateAndReleaseFlowFromTable: boolean = false;
  @Input() confirmationPagePhase2: boolean = false;
  eAuthClientsResponse: EAuthClientsResponse;
  readonly ineligibleEAuthorizationDetailsCopy = Constants.ineligibleEAuthorizationDetailsCopy;
  readonly clientEauthTooltipText = Constants.eAuthorizationDescription;
  readonly schwabAllianceTooltipText = Constants.schwabAllianceDescription;
  readonly eauthErrorText = Constants.eauthServiceError;
  readonly mmIneligibleAccountPageHeaderText = Constants.mmIneligibleAccountPageHeader;
  readonly eAuthTextD2P: string = eAuthNotifications.digitalToPdf;
  firstPartyWPfaInfo: boolean;
  eAuthError$: Observable<boolean>;
  destroy$: Subject<boolean> = new Subject<boolean>();
  eAuthServiceError: boolean = false;
  shouldDisplayEauthTable: boolean;
  useTestTrustDigitalToPdfFlow: boolean;
  itsInfo: InternalTransfersInfo;
  toAccountsById: ToAccountInfoById;
  toAccounts: string[];
  clientAuthBannerMessageText: string;
  regTypeShortName: string;
  transactionType: string;
  displayBanner: boolean;
  isOneTimeTotalDist: boolean;
  toAccountsData: {
    accountId: string;
    accountRegistration: string;
    regType: string;
  }[] = [];
  regType: string;
  useTestTrustOrEstateDigitalToPdfFlow: boolean = false;
  isChecksBundledOneTimeTotalDist: boolean;
  is401kToAccount: boolean;
  isPalAccount: boolean = false;

  constructor(private store: Store<{}>, private flagService: FeatureFlagService, private coreService: CoreService, private validateWireFlagsService: ValidateWireFlagsService) { }

  ngOnInit(): void {
    this.isPalAccount = isPalAccountFlow(this.coreService.currentContext?.contextType, this.validateWireFlagsService.enablePalWireCreateTransaction());
    this.coreService.currentContextUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        this.isPalAccount = isPalAccountFlow(context?.contextType, this.validateWireFlagsService.enablePalWireCreateTransaction());
      });

    this.eAuthError$ = this.store.select(selectEAuthError);

    combineLatest([
      this.store.select(selectToAccountsInfo),
      this.store.select(selectInternalTransfersInfo),
      this.store.select(selectAccountInfo),
      this.store.select(selectTransferType)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([toAccounts, itsInfo, accountInfo, transferType]) => {
      this.toAccountsById = toAccounts;
      this.toAccounts = Object.keys(toAccounts);
      this.itsInfo = itsInfo;
      this.firstPartyWPfaInfo = accountInfo.info.pfaDetails.hasWpfaRole;
      this.toAccounts.forEach((account) => {
        this.regTypeShortName = this.toAccountsById[account].regTypeShortName;
      });

      if (transferType !== transferTypes.CASH) {
        this.toAccountsData = [];
        itsInfo.multipleToJournalsList.forEach((toAccountDetails) => {
          if (!toAccountDetails.isThirdParty) {
            this.toAccountsData.push({
              accountId: toAccountDetails.toAccountInfo.accountNumber ?? '',
              accountRegistration:
                toAccountDetails.toAccountInfo.accountRegistration[0] ?? '',
              regType: toAccountDetails.toAccountInfo.regTypeShortName ?? '',
            });
          }
          this.displayBanner = this.displayAdditionalAuthBanner(
            toAccountDetails.isThirdParty
          );
          if (this.displayBanner) {
            this.clientAuthBannerMessageText =
              Constants.clientAuthText + this.showAccountDetails();
          }
        });
      }
    });

    combineLatest([
      this.store.select(selectEAuthClients),
      this.store.select(selectCreateStandingAuthEAuthClients)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([eAuthClientsResponse, createStandingAuthEauthClientsResponse]) => {
      this.eAuthClientsResponse = this.standingAuthorizationsEAuth ? createStandingAuthEauthClientsResponse : eAuthClientsResponse;
    });

    combineLatest([
      this.store.select(selectEauthClientServiceError),
      this.store.select(selectCreateStandingAuthEauthClientServiceError)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([eauthClientsError, createStandingAuthEauthClientError]) => {
      this.eAuthServiceError = this.standingAuthorizationsEAuth ? createStandingAuthEauthClientError : eauthClientsError;
    });

    this.store.select(selectUseTestTrustDigitalToPdfFlow).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.useTestTrustDigitalToPdfFlow = val;
    });

    this.store.select(selectRegType).pipe(takeUntil(this.destroy$)).subscribe(regType => {
      this.regType = regType;
    });

    combineLatest([
      this.store.select(selectIs401kToAccount),
      this.store.select(selectisStandingAuthI401KToAccount),
    ]).pipe(takeUntil(this.destroy$)).subscribe(([is401kToAccount, isStandingAuthI401kToAccount]) => {
      this.is401kToAccount = is401kToAccount || isStandingAuthI401kToAccount;
    });

    combineLatest([
      this.store.select(selectTransactionType),
      this.store.select(selectStagedTransactionForm)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([ transactionTypeSloa, stagedTransactionType]) => {
      this.transactionType = transactionTypeSloa === null || transactionTypeSloa === '' ? stagedTransactionType.transactionType : transactionTypeSloa ?? '';
    });

    combineLatest([
      this.store.select(selectStagedTransactionForm),
      this.store.select(selectIsBundledOneTimeTotalDist)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([val, isBundledOnetimeTotalDist]) => {
      this.isOneTimeTotalDist = isItExtraPrompts(val.extraPrompts) && val.extraPrompts.frequency === "onetimeTotalDist";
      this.isChecksBundledOneTimeTotalDist = isBundledOnetimeTotalDist;
      if (this.transactionType == null || this.transactionType == undefined || this.transactionType == '') {
        this.transactionType = val.transactionType;
      }
    });

    combineLatest([
      this.store.select(selectVerifyCallFinished),
      this.store.select(selectSLOAVerifyCallFinished)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([verifyCallFinished, sloaVerifyCallFinsished]) => {
      if ((verifyCallFinished || sloaVerifyCallFinsished) && this.isInitiateAndReleaseFlowFromTable) {
        this.shouldDisplayEauthTable = this.displayEauthTable();
      }
    });

    if (!this.isInitiateAndReleaseFlowFromTable) {
      this.shouldDisplayEauthTable = this.displayEauthTable();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  // to return account details of recipients who donot require additional authorization
  showAccountDetails(): string {
    return this.toAccountsData
      .map(
        (account) =>
          account.accountRegistration +
          ' (' +
          account.accountId +
          ' | ' +
          account.regType +
          ')'
      )
      .join('; ');
  }

  get enableAuthForDigitalGoing(): boolean {
    return !this.eAuthClientsResponse.areAllAccountHoldersIneligible;
  }

  displayEauthTable(): boolean {
    // Going-digital flag removed; checks are now always enabled for digital reg types

    if (this.transactionType !== transactionTypes.check && (this.isOneTimeTotalDist || this.is401kToAccount)) {
      return false;
    }

    if (this.transactionType === transactionTypes.check && (this.isOneTimeTotalDist || this.isChecksBundledOneTimeTotalDist)) {
      return false;
    }

    // Account type restrictions (like isAccountRestricted) take precedence
    if (this.accountTypeIneligibleForEAuth) {
      this.store.dispatch(EAuthActions.eAuthTableDisplay({ userInputRequired: false }));
      return false;
    }

    // Show eAuth table if account holders are eligible
    if (!this.accountHoldersIneligibleForEAuth) {
      this.store.dispatch(EAuthActions.eAuthTableDisplay({ userInputRequired: true }));
      return true;
    } else {
      this.store.dispatch(EAuthActions.eAuthTableDisplay({ userInputRequired: false }));
      return false;
    }
  }

  displayAdditionalAuthBanner(isThirdParty: boolean): boolean {
    if (this.itsInfo.hasWpfaRole && !isThirdParty && this.firstPartyWPfaInfo) {
      return true;
    }
    return false;
  }

  get accountTypeIneligibleForEAuth(): boolean {
    return this.eAuthClientsResponse.isAccountRestricted;
  }

  get accountHoldersIneligibleForEAuth(): boolean {
    if (!this.eAuthClientsResponse || !this.eAuthClientsResponse.eauthClients || this.eAuthClientsResponse.eauthClients.length <= 0) {
      return true;
    }
    for (var i = 0; i < this.eAuthClientsResponse.eauthClients.length; i++) {
      if (this.eAuthClientsResponse.eauthClients[i].displayIndicator) {
        return false;
      }
    }
    return true;
  }

  get showSubmitEmailAddressButton(): boolean {
    return this.isInitiateAndReleaseFlowFromTable ? false : true;
  }

  displayAuthorizationTitle(): boolean {
    if (this.transactionType === transactionTypes.check) {
      return true;
    }

    if (this.transactionType === 'internal-transfer' && this.isOneTimeTotalDist) {
      return true;
    }

    if (!this.shouldDisplayEauthTable && !(this.transactionType === transactionTypes.internalTransfer || this.transactionType === transactionTypes.wire)) return false;

    return true;
  }
}
